import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { PRODUCT2 } from '../data/products';

export default function FaceWashSection() {
  const { t, lang } = useLang();
  const fw = t.facewash;
  const isAr = lang === 'ar';
  const [expanded, setExpanded] = useState(false);

  return (
    <section id="facewash" className="py-24 bg-misky-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-green-400 text-xs uppercase tracking-widest mb-3">{fw.sectionLabel}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white">{fw.title}</h2>
          <p className="text-misky-light text-sm mt-3 uppercase tracking-widest">{fw.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left: Visual + label mockup */}
          <div className="flex flex-col items-center">
            {/* Product visual */}
            <div className="relative w-56 h-56 rounded-[40px] bg-gradient-to-br from-[#052e16] to-[#166534] border border-green-900/60 flex flex-col items-center justify-center shadow-2xl mb-8">
              <div className="text-7xl mb-2">🌿</div>
              <div className="text-white font-bold text-lg tracking-widest">MISKY</div>
              <div className="text-green-300 text-xs tracking-wider mt-1">TEA TREE & CHAMOMILE</div>
              <div className="absolute bottom-4 text-green-400/60 text-xs">160 ml</div>
            </div>

            {/* Key benefits icons */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
              {[
                { icon: '🌱', ar: 'يزيل الشوائب والزيوت الزائدة', en: 'Removes impurities & excess oils' },
                { icon: '🌼', ar: 'يمنح إحساساً بالراحة والانتعاش', en: 'Soothes & refreshes skin' },
                { icon: '✨', ar: 'يساعد على تنظيف البشرة بلطف', en: 'Gently cleanses skin' },
                { icon: '🔒', ar: 'مخصص للاستخدام الخارجي فقط', en: 'For external use only' },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl p-3">
                  <span className="text-xl flex-shrink-0">{b.icon}</span>
                  <span className="text-white/70 text-xs leading-tight">{isAr ? b.ar : b.en}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Details */}
          <div>
            {/* Description */}
            <p className="text-misky-light text-sm leading-relaxed mb-10">
              {isAr ? PRODUCT2.descriptionAr : PRODUCT2.descriptionEn}
            </p>

            {/* How to use */}
            <div className="mb-10">
              <h3 className="text-white font-bold text-lg mb-5">{fw.howToUse}</h3>
              <div className="space-y-4">
                {fw.steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-2xl bg-green-900/40 border border-green-800/60 flex items-center justify-center flex-shrink-0 text-xl">
                      {step.icon}
                    </div>
                    <div className="flex-1 pt-2">
                      <span className="text-misky-light text-sm leading-relaxed">{step.text}</span>
                    </div>
                    <div className="w-6 h-6 rounded-full bg-green-900/30 border border-green-700/40 flex items-center justify-center flex-shrink-0 mt-2">
                      <span className="text-green-400 text-xs font-bold">{i + 1}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Warnings */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h4 className="text-white/80 text-xs uppercase tracking-widest mb-3">{fw.warningsLabel}</h4>
              <ul className="space-y-2">
                {fw.warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-misky-light text-xs leading-relaxed">
                    <span className="text-yellow-500 mt-0.5 flex-shrink-0">⚠</span>
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Key Ingredients */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <p className="text-green-400 text-xs uppercase tracking-widest mb-2">{fw.ingredientsLabel}</p>
            <h3 className="text-3xl font-bold text-white">{fw.keyIngredients}</h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {fw.keyItems.map((item, i) => (
              <div key={i} className="p-5 rounded-2xl bg-white/5 border border-white/10 text-center hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-green-900/40 border border-green-800/60 flex items-center justify-center text-2xl">
                  {item.icon}
                </div>
                <h4 className="text-white font-semibold text-sm mb-2 leading-tight">{item.name}</h4>
                <p className="text-misky-light text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Full ingredients */}
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
            <h4 className="text-white font-bold mb-4">{fw.fullListLabel}</h4>
            <p className={`text-misky-light text-sm leading-relaxed ${!expanded ? 'line-clamp-3' : ''}`}>
              {PRODUCT2.ingredients}
            </p>
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-4 text-xs text-white/50 hover:text-white underline underline-offset-4 transition-colors"
            >
              {expanded ? fw.showLess : fw.showFull}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
